import { useState, useEffect } from 'react';

interface AnimatedHeadingProps {
  text: string;
  className?: string;
  initialDelay?: number;
  charDelay?: number;
  duration?: number;
}

export default function AnimatedHeading({
  text,
  className = '',
  initialDelay = 200,
  charDelay = 30,
  duration = 500,
}: AnimatedHeadingProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), initialDelay);
    return () => clearTimeout(timer);
  }, [initialDelay]);

  const lines = text.split('\n');

  return (
    <span className={className} aria-label={text}>
      {lines.map((line, lineIndex) => {
        const prevLineChars = lines
          .slice(0, lineIndex)
          .reduce((acc, l) => acc + l.length, 0);

        // Split the line into words while preserving spaces as their own tokens,
        // so each word becomes a non-breaking unit and lines wrap only at spaces.
        const tokens = line.split(/(\s+)/).filter(t => t.length > 0);
        let charCursor = 0;

        return (
          <span key={lineIndex} className="block">
            {tokens.map((token, tokenIndex) => {
              const isSpace = /^\s+$/.test(token);
              const tokenStart = charCursor;
              charCursor += token.length;

              if (isSpace) {
                return (
                  <span key={tokenIndex} aria-hidden="true">
                    {'\u00A0'.repeat(token.length)}
                  </span>
                );
              }

              return (
                <span
                  key={tokenIndex}
                  className="inline-block whitespace-nowrap"
                >
                  {token.split('').map((char, charIndex) => {
                    const globalIndex = prevLineChars + tokenStart + charIndex;
                    const delay = globalIndex * charDelay;

                    return (
                      <span
                        key={charIndex}
                        className="inline-block"
                        style={{
                          opacity: visible ? 1 : 0,
                          transform: visible ? 'translateX(0)' : 'translateX(-18px)',
                          transition: `opacity ${duration}ms ease, transform ${duration}ms ease`,
                          transitionDelay: `${delay}ms`,
                        }}
                      >
                        {char}
                      </span>
                    );
                  })}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}
